import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  CooperationData,
  CooperationDataInterface,
} from '../../../../interfaces/contact-data.interface';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService } from '../../../services/api.service';
import { RecaptchaService } from '../../../services/recaptcha.service';
import { ConfirmationService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { environment } from '../../../../environments/environment';
import {
  captureFormSubmitException,
  formFieldsSentryExtra,
} from '../../../utils/sentry-http-error.util';

@Component({
  selector: 'app-cooperation',
  templateUrl: './cooperation.component.html',
  styleUrls: ['./cooperation.component.scss'],
})
export class CooperationComponent implements OnInit, OnDestroy {
  cooperationDto: CooperationDataInterface;
  cooperationFormGroup: UntypedFormGroup;
  recaptchaPending = false;
  submitThanks = false;
  thankYouCooldown = false;
  private thankYouTimer: ReturnType<typeof setTimeout> | null = null;
  readonly formEnabled: boolean;

  constructor(
    private fb: UntypedFormBuilder,
    private apiService: ApiService,
    private recaptchaService: RecaptchaService,
    private confirmationService: ConfirmationService,
    private transloco: TranslocoService
  ) {
    this.formEnabled = this.recaptchaService.isConfigured();
  }

  get submitInactive(): boolean {
    return (
      !this.formEnabled ||
      this.recaptchaPending ||
      this.thankYouCooldown ||
      this.cooperationFormGroup?.invalid
    );
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    if (this.thankYouTimer) {
      clearTimeout(this.thankYouTimer);
    }
  }

  warmRecaptcha(): void {
    this.recaptchaService.warmup();
  }

  initializeForm(): void {
    this.cooperationDto = new CooperationData();
    this.cooperationFormGroup = this.fb.group({
      name: [this.cooperationDto.name, [Validators.required]],
      taxNo: [
        this.cooperationDto.taxNo,
        [Validators.required, RxwebValidators.digit()],
      ],
      phoneNo: [
        this.cooperationDto.phoneNo,
        [Validators.required, RxwebValidators.digit()],
      ],
      email: [
        this.cooperationDto.email,
        [Validators.required, RxwebValidators.email()],
      ],
      textMsg: [this.cooperationDto.textMsg, [Validators.required]],
      agreement1: [this.cooperationDto.agreement1, [Validators.requiredTrue]],
      agreement2: [this.cooperationDto.agreement2, [Validators.requiredTrue]],
    });
  }

  async onSubmit(): Promise<void> {
    if (this.submitInactive) {
      return;
    }

    this.recaptchaPending = true;
    this.submitThanks = false;
    try {
      const recaptchaToken = await this.recaptchaService.execute(
        'cooperation_form',
      );
      this.cooperationDto = new CooperationData({
        ...this.cooperationFormGroup.value,
        recaptchaToken,
      });
      await this.apiService
        .sendCooperationEmail(this.cooperationDto)
        .pipe(take(1))
        .toPromise();
      this.initializeForm();
      this.showThanks();
    } catch (error: unknown) {
      this.reportSubmitFailure(
        error,
        error instanceof HttpErrorResponse ? 'api' : 'recaptcha',
      );
      if (error instanceof HttpErrorResponse) {
        this.handleSubmitError(error);
      } else {
        console.warn(
          '[GAP cooperation form] reCAPTCHA before submit failed',
          error,
        );
        this.confirmationService.confirm({
          message: this.transloco.translate('contact.confirmation.error'),
          rejectVisible: false,
          acceptLabel: this.transloco.translate('contact.confirmation.accept'),
        });
      }
    } finally {
      this.recaptchaPending = false;
    }
  }

  private showThanks(): void {
    this.submitThanks = true;
    this.thankYouCooldown = true;
    if (this.thankYouTimer) {
      clearTimeout(this.thankYouTimer);
    }
    this.thankYouTimer = setTimeout(() => {
      this.thankYouCooldown = false;
      this.thankYouTimer = null;
    }, 2000);
  }

  private reportSubmitFailure(
    error: unknown,
    phase: 'recaptcha' | 'api',
  ): void {
    const httpError = error instanceof HttpErrorResponse ? error : null;
    captureFormSubmitException(error, {
      form: 'gap_cooperation',
      action: 'cooperation_form',
      phase,
      site: 'gaponline.pl',
      status: httpError ? httpError.status : 0,
      apiBase: environment.apiBase,
      production: environment.production,
      formEnabled: this.formEnabled,
      hostname:
        typeof window !== 'undefined' ? window.location.hostname : null,
      href: typeof window !== 'undefined' ? window.location.href : null,
      recaptchaTokenPresent: Boolean(this.cooperationDto?.recaptchaToken),
      recaptchaTokenLength: this.cooperationDto?.recaptchaToken?.length ?? 0,
      ...formFieldsSentryExtra(this.cooperationFormGroup),
    });
  }

  private handleSubmitError(error: HttpErrorResponse): void {
    if (!environment.production) {
      console.warn('[GAP cooperation form] API error', {
        status: error.status,
        body: error.error,
      });
    }

    if (error.status === 403) {
      const agreement1 = this.cooperationFormGroup.get('agreement1');
      if (!agreement1.value) {
        agreement1.markAsDirty();
        agreement1.markAllAsTouched();
      }

      const agreement2 = this.cooperationFormGroup.get('agreement2');
      if (!agreement2.value) {
        agreement1.markAsDirty();
        agreement1.markAllAsTouched();
      }
      return;
    }

    this.confirmationService.confirm({
      message: this.transloco.translate('contact.confirmation.error'),
      rejectVisible: false,
      acceptLabel: this.transloco.translate('contact.confirmation.accept'),
    });
  }
}
